import { useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { Lock, Loader2, LogOut, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { useToast } from '@/hooks/use-toast';
import { getSignInErrorDescription } from '@/lib/authErrorMessages';

interface SessionLockScreenProps {
  userId: string;
  userEmail: string;
  /** An IdP-authenticated session cannot be safely revalidated with a password. */
  isSsoSession?: boolean;
  onUnlock: () => void;
  onMFARequired: (factorId: string) => void;
  onFullLogout: () => void;
}

export function SessionLockScreen({
  userId,
  userEmail,
  isSsoSession = false,
  onUnlock,
  onMFARequired,
  onFullLogout,
}: SessionLockScreenProps) {
  const { toast } = useToast();
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const createPasswordValidationClient = () => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabasePublishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

    if (!supabaseUrl || !supabasePublishableKey) {
      throw new Error('Supabase environment is not configured');
    }

    return createClient<Database>(supabaseUrl, supabasePublishableKey, {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false,
        storageKey: 'session-lock-password-validation',
      },
    });
  };

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;

    setIsLoading(true);
    let passwordValidationClient:
      | ReturnType<typeof createPasswordValidationClient>
      | null = null;
    try {
      passwordValidationClient = createPasswordValidationClient();

      // Validate the password against an isolated auth client so we do not
      // replace the active in-app session while the lock screen is open.
      const { error } = await passwordValidationClient.auth.signInWithPassword({
        email: userEmail,
        password,
      });

      if (error) {
        toast({
          title: 'Authentication Failed',
          description: getSignInErrorDescription(error.message),
          variant: 'destructive',
        });
        setPassword('');
        setIsLoading(false);
        return;
      }

      const { isMfaRequired } = await import('@/lib/auth');
      const requiresAccountMfa = isMfaRequired(userEmail);

      const { data: mfaData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      const requiresStepUp = mfaData?.nextLevel === 'aal2' && mfaData?.currentLevel === 'aal1';

      if (requiresAccountMfa || requiresStepUp) {
        const { data: factorsData } = await supabase.auth.mfa.listFactors();
        const totpFactor = factorsData?.totp?.find((f) => f.status === 'verified');

        if (totpFactor) {
          onMFARequired(totpFactor.id);
          setIsLoading(false);
          return;
        }

        if (requiresAccountMfa) {
          toast({
            title: 'MFA Required',
            description: 'This account must complete two-factor authentication before unlocking the session.',
            variant: 'destructive',
          });
          setPassword('');
          setIsLoading(false);
          return;
        }
      }

      // No MFA required or already verified on the active session.
      onUnlock();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      if (passwordValidationClient) {
        try {
          await passwordValidationClient.auth.signOut({ scope: 'local' });
        } catch {
          // Ignore cleanup failures on the isolated client.
        }
      }
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-background">
      <div className="flex min-h-dvh items-center justify-center px-4 py-6 sm:px-6 lg:px-8 xl:px-10">
        <Card className="w-full max-w-md border-border/50 shadow-xl">
          <CardHeader className="pb-5 text-center sm:pb-6">
            <div className="mb-4 flex justify-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/60">
                <img
                  src="/platform-logo-white.svg"
                  alt="Supply Guard"
                  className="h-6 w-6"
                />
              </div>
            </div>
            <CardTitle className="text-2xl">Session Locked</CardTitle>
            <CardDescription>
              {isSsoSession
                ? 'Your session was locked due to inactivity. Continue to your organisation sign-in page to reauthenticate.'
                : 'Your session was locked due to inactivity. Re-enter your password to continue.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isSsoSession ? (
              <div className="space-y-4">
                <p className="text-center text-sm text-muted-foreground">
                  Signed in as {userEmail}
                </p>
                <Button type="button" className="w-full" onClick={onFullLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Continue to Sign In
                </Button>
              </div>
            ) : (
              <form onSubmit={handleUnlock} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="lock-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="lock-email"
                      value={userEmail}
                      readOnly
                      disabled
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lock-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="lock-password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoFocus
                      autoComplete="current-password"
                      className="pl-10"
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={!password || isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Unlocking...
                    </>
                  ) : (
                    'Unlock Session'
                  )}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-muted-foreground"
                  onClick={onFullLogout}
                  disabled={isLoading}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Cancel and Sign Out
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
